import React, { useState, useEffect } from "react";
import {
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  SafeAreaView,
  StatusBar,
  Dimensions,
  Modal,
} from "react-native";
import { ScrollView } from "react-native-gesture-handler";
import Icon from "react-native-vector-icons/Feather";
import { useDispatch, useSelector } from "react-redux";
import { useToast } from "react-native-toast-notifications";
import { useIsFocused, useNavigation } from "@react-navigation/native";
import { fetchOperationHistoryListById } from "../../../services/operationService";
import { OperationsDispatcher } from "../../../redux/dispatcher/operationsDispatcher";
import { actionTypesOperations } from "../../../redux/constants/actionTypesOperations";
import Spinner from "../../../components/shared/Spinner";

const { width: screenWidth } = Dimensions.get("window");

// Responsive breakpoints
const isSmallScreen = screenWidth < 760;
const isMediumScreen = screenWidth >= 1080 && screenWidth < 760;

// Responsive values
const getResponsiveValue = (small: number, medium: number, large: number) => {
  if (isSmallScreen) return small;
  if (isMediumScreen) return medium;
  return large;
};

interface OperationHistoryScreenProps {
  visible: boolean;
  onClose: () => void;
  order: any;
}

const OperationHistoryScreen: React.FC<OperationHistoryScreenProps> = ({
  visible,
  onClose,
  order,
}) => {
  const [refreshing, setRefreshing] = useState(false);
  const [isCompactView, setIsCompactView] = useState(screenWidth < 414);
  const [expandedCards, setExpandedCards] = useState<Set<string>>(new Set());
  const toast = useToast();

  const {
    isOperationHistoryLoaded,
    operationHistoryList,
    totalCount,
    totalPages,
    pageSize,
    page,
    sort,
    status,
  } = useSelector((state: any) => state.operations.operationAuditConfig);

  const isFocused = useIsFocused();

  // Handle screen rotation
  useEffect(() => {
    const updateLayout = () => {
      const { width } = Dimensions.get("window");
      setIsCompactView(width < 414);
    };

    const subscription = Dimensions.addEventListener("change", updateLayout);
    return () => subscription?.remove();
  }, []);

  useEffect(() => {
    if (isFocused && order) {
      fetchHistory();
    }
  }, [isFocused, sort, status, pageSize, page, order]);

  const fetchHistory = async () => {
    try {
      await fetchOperationHistoryListById(order._id, {
        page: page,
        pageSize: pageSize,
        sort: sort.direction === "asc" ? sort.key : "-" + sort.key,
        status: status,
      });
    } catch (error) {
      toast.show("Error fetching operation history", { type: "danger" });
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await fetchHistory();
    } finally {
      setRefreshing(false);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();

    if (now.getTime() - date.getTime() < 24 * 60 * 60 * 1000) {
      const hours = Math.floor(
        (now.getTime() - date.getTime()) / (60 * 60 * 1000)
      );
      if (hours < 1) {
        const minutes = Math.floor(
          (now.getTime() - date.getTime()) / (60 * 1000)
        );
        return `${minutes}m ago`;
      }
      return `${hours}h ago`;
    }

    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: date.getFullYear() !== now.getFullYear() ? "numeric" : undefined,
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatFieldName = (fieldName: string) => {
    return fieldName
      .replace(/___/g, " > ")
      .replace(/_/g, " ")
      .split(" ")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  const getActionIcon = (action: string) => {
    switch (action) {
      case "CREATE":
        return { name: "plus-circle", color: "#10B981" };
      case "UPDATE":
        return { name: "edit-3", color: "#3B82F6" };
      case "DELETE":
        return { name: "trash-2", color: "#EF4444" };
      default:
        return { name: "activity", color: "#6B7280" };
    }
  };

  const handleSort = (key: any) => {
    let direction = "asc";
    if (sort.key === key && sort.direction === "asc") {
      direction = "desc";
    }
    OperationsDispatcher(actionTypesOperations.UpdateOperationAuditSort, {
      key: key,
      direction: direction,
    });
  };

  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= totalPages) {
      OperationsDispatcher(
        actionTypesOperations.UpdateOperationAuditPage,
        newPage
      );
    }
  };

  const toggleCardExpansion = (cardId: string) => {
    const newExpanded = new Set(expandedCards);
    if (newExpanded.has(cardId)) {
      newExpanded.delete(cardId);
    } else {
      newExpanded.add(cardId);
    }
    setExpandedCards(newExpanded);
  };

  const SortIcon = ({ column }: any) => {
    const iconName =
      sort.key === column
        ? sort.direction === "asc"
          ? "chevron-up"
          : "chevron-down"
        : "minus";

    const iconColor = sort.key === column ? "#3B82F6" : "#9CA3AF";

    return (
      <Icon
        name={iconName}
        size={getResponsiveValue(12, 14, 16)}
        color={iconColor}
      />
    );
  };

  // Card View for smaller screens
  const HistoryCard = ({ item }: any) => {
    const icon = getActionIcon(item.action);
    const isExpanded = expandedCards.has(item._id);
    const changesCount = Object.keys(item.changes || {}).length;

    return (
      <View style={styles.historyCard}>
        <View style={styles.cardHeader}>
          <View style={styles.cardIcon}>
            <Icon name={icon.name} size={20} color={icon.color} />
          </View>
          <View style={styles.cardHeaderText}>
            <Text style={styles.cardAction}>{item.action}</Text>
            <Text style={styles.cardDate}>{formatDate(item.modifiedAt)}</Text>
            <Text style={styles.cardUser}>by {item.modifiedBy}</Text>
          </View>
          <TouchableOpacity
            style={styles.expandButton}
            onPress={() => toggleCardExpansion(item._id)}
          >
            <Icon
              name={isExpanded ? "chevron-up" : "chevron-down"}
              size={16}
              color="#6B7280"
            />
          </TouchableOpacity>
        </View>

        <View style={styles.cardSummary}>
          <Text style={styles.summaryText}>
            {changesCount} field{changesCount !== 1 ? "s" : ""} modified
          </Text>
        </View>

        {isExpanded && (
          <View style={styles.cardChanges}>
            <Text style={styles.changesTitle}>Changes Made:</Text>
            {Object.entries(item.changes || {}).map(
              ([field, change]: [string, any]) => (
                <View key={field} style={styles.changeItem}>
                  <Text style={styles.fieldName}>{formatFieldName(field)}</Text>

                  {change.before !== undefined && (
                    <View style={styles.changeRow}>
                      <Text style={styles.changeLabel}>Before:</Text>
                      <Text style={styles.beforeValue}>
                        {change.before || "(empty)"}
                      </Text>
                    </View>
                  )}

                  {change.after !== undefined && (
                    <View style={styles.changeRow}>
                      <Text style={styles.changeLabel}>After:</Text>
                      <Text style={styles.afterValue}>
                        {change.after || "(empty)"}
                      </Text>
                    </View>
                  )}
                </View>
              )
            )}
          </View>
        )}

        {item.metadata && (
          <View style={styles.cardMetadata}>
            <Text style={styles.metadataText}>
              IP: {item.metadata.ipAddress}
            </Text>
          </View>
        )}
      </View>
    );
  };

  // Table View for larger screens
  const DataTableHeader = () => (
    <View style={styles.tableHeader}>
      <View style={styles.actionColumn}>
        <Text style={styles.headerText}>Action</Text>
      </View>
      <TouchableOpacity
        style={styles.userColumn}
        onPress={() => handleSort("modifiedAt")}
      >
        <Text style={styles.headerText}>Date</Text>
        <SortIcon column="modifiedAt" />
      </TouchableOpacity>
      <TouchableOpacity
        style={styles.userColumn}
        onPress={() => handleSort("modifiedBy")}
      >
        <Text style={styles.headerText}>User</Text>
        <SortIcon column="modifiedBy" />
      </TouchableOpacity>

      <TouchableOpacity style={styles.userColumn}>
        <Text style={styles.headerText}>Source</Text>
        <SortIcon column="auditFrom" />
      </TouchableOpacity>

      <View style={styles.changesColumn}>
        <Text style={styles.headerText}>Changes</Text>
      </View>
      <View style={styles.detailsColumn}>
        <Text style={styles.headerText}>Details</Text>
      </View>
    </View>
  );

  const DataTableRow = ({ item, index }: any) => {
    const icon = getActionIcon(item.action);
    const isEven = index % 2 === 0;
    const isExpanded = expandedCards.has(item._id);
    const changesCount = Object.keys(item.changes || {}).length;

    return (
      <View>
        <View style={[styles.tableRow, isEven && styles.evenRow]}>
          <View style={styles.actionColumn}>
            <View style={styles.actionIndicator}>
              <Text style={[styles.actionText, { color: icon.color }]}>
                {item.action}
              </Text>
            </View>
          </View>

          <View style={styles.dateColumn}>
            <Text style={styles.dateText}>{formatDate(item.modifiedAt)}</Text>
          </View>

          <View style={styles.userColumn}>
            <Text style={styles.userText}>{item.modifiedBy}</Text>
          </View>

          <View style={styles.userColumn}>
            <Text style={styles.userText}>{item.auditFrom || "Msteps"}</Text>
          </View>

          <View style={styles.changesColumn}>
            <Text style={styles.changesText}>
              {changesCount} field{changesCount !== 1 ? "s" : ""}
            </Text>
          </View>

          <View style={styles.detailsColumn}>
            <TouchableOpacity
              style={styles.detailsButton}
              onPress={() => toggleCardExpansion(item._id)}
            >
              <Icon
                name={isExpanded ? "chevron-up" : "chevron-down"}
                size={14}
                color="#3B82F6"
              />
              <Text style={styles.detailsButtonText}>
                {isExpanded ? "Hide" : "Show"}
              </Text>
            </TouchableOpacity>
          </View>
        </View>

        {isExpanded && (
          <View style={styles.expandedDetails}>
            <Text style={styles.expandedTitle}>Field Changes:</Text>
            {Object.entries(item.changes || {}).map(
              ([field, change]: [string, any]) => (
                <View key={field} style={styles.expandedChangeItem}>
                  <Text style={styles.expandedFieldName}>
                    {formatFieldName(field)}:
                  </Text>
                  <View style={styles.expandedChangeValues}>
                    {change.before !== undefined && (
                      <Text style={styles.expandedBefore}>
                        Before: {change.before || "(empty)"}
                      </Text>
                    )}
                    {change.after !== undefined && (
                      <Text style={styles.expandedAfter}>
                        After: {change.after || "(empty)"}
                      </Text>
                    )}
                  </View>
                </View>
              )
            )}
            {item.metadata && (
              <Text style={styles.expandedMetadata}>
                IP Address: {item.metadata.ipAddress}
              </Text>
            )}
          </View>
        )}
      </View>
    );
  };

  const PaginationControls = () => {
    const startPage = Math.max(1, page - 2);
    const endPage = Math.min(totalPages, startPage + 4);
    const pages = [];

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }

    return (
      <View style={styles.paginationContainer}>
        <View style={styles.paginationInfo}>
          <Text style={styles.paginationText}>
            Showing {(page - 1) * pageSize + 1} to{" "}
            {Math.min(page * pageSize, totalCount)} of {totalCount} entries
          </Text>
        </View>

        <View style={styles.paginationControls}>
          <TouchableOpacity
            style={[
              styles.paginationButton,
              page === 1 && styles.disabledButton,
            ]}
            onPress={() => handlePageChange(page - 1)}
            disabled={page === 1}
          >
            <Icon
              name="chevron-left"
              size={16}
              color={page === 1 ? "#9CA3AF" : "#374151"}
            />
          </TouchableOpacity>

          {pages.map((pageNum) => (
            <TouchableOpacity
              key={pageNum}
              style={[
                styles.paginationButton,
                page === pageNum && styles.activePage,
              ]}
              onPress={() => handlePageChange(pageNum)}
            >
              <Text
                style={[
                  styles.paginationButtonText,
                  page === pageNum && styles.activePageText,
                ]}
              >
                {pageNum}
              </Text>
            </TouchableOpacity>
          ))}

          <TouchableOpacity
            style={[
              styles.paginationButton,
              page === totalPages && styles.disabledButton,
            ]}
            onPress={() => handlePageChange(page + 1)}
            disabled={page === totalPages}
          >
            <Icon
              name="chevron-right"
              size={16}
              color={page === totalPages ? "#9CA3AF" : "#374151"}
            />
          </TouchableOpacity>
        </View>
      </View>
    );
  };

  return (
    <Modal visible={visible} transparent animationType="slide">
      <SafeAreaView style={styles.container}>
        <StatusBar barStyle="dark-content" backgroundColor="#FFFFFF" />

        {/* Header */}
        <View style={styles.header}>
          <TouchableOpacity style={styles.backButton} onPress={onClose}>
            <Icon name="arrow-left" size={20} color="#374151" />
          </TouchableOpacity>

          <View style={styles.headerContent}>
            <Text style={styles.title}>Operation History</Text>
            <Text style={styles.subtitle}>
              {order?.documentNumber} • {totalCount} entries
            </Text>
          </View>

          <View style={styles.headerActions}>
            <TouchableOpacity
              style={styles.refreshButton}
              onPress={handleRefresh}
              disabled={refreshing}
            >
              <Icon
                name="refresh-cw"
                size={getResponsiveValue(16, 18, 20)}
                color="#6B7280"
              />
            </TouchableOpacity>
          </View>
        </View>

        {/* Content */}
        <View style={styles.contentContainer}>
          {!isOperationHistoryLoaded ? (
            <View style={styles.loadingContainer}>
              <Spinner size="large" color="#007bff" />
            </View>
          ) : (
            <>
              {operationHistoryList.length === 0 ? (
                <View style={styles.emptyState}>
                  <Icon name="clock" size={48} color="#D1D5DB" />
                  <Text style={styles.emptyTitle}>No history found</Text>
                  <Text style={styles.emptySubtitle}>
                    No audit trail available for this operation
                  </Text>
                </View>
              ) : (
                <>
                  {isCompactView ? (
                    // Card View for smaller screens
                    <ScrollView style={styles.cardContainer}>
                      {operationHistoryList.map((item: any, index: number) => (
                        <HistoryCard key={item._id} item={item} index={index} />
                      ))}
                    </ScrollView>
                  ) : (
                    // Table View for larger screens
                    <View style={styles.tableContainer}>
                      <DataTableHeader />
                      <ScrollView style={styles.tableBody}>
                        {operationHistoryList.map(
                          (item: any, index: number) => (
                            <DataTableRow
                              key={item._id}
                              item={item}
                              index={index}
                            />
                          )
                        )}
                      </ScrollView>
                    </View>
                  )}
                </>
              )}

              {<PaginationControls />}
            </>
          )}
        </View>
      </SafeAreaView>
    </Modal>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: "#F9FAFB",
  },
  header: {
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: getResponsiveValue(16, 20, 24),
    paddingVertical: getResponsiveValue(12, 16, 20),
    backgroundColor: "#FFFFFF",
    borderBottomWidth: 1,
    borderBottomColor: "#E5E7EB",
  },
  backButton: {
    padding: 8,
    marginRight: 12,
  },
  headerContent: {
    flex: 1,
  },
  title: {
    fontSize: getResponsiveValue(20, 24, 28),
    fontWeight: "700",
    color: "#111827",
    marginBottom: 4,
  },
  subtitle: {
    fontSize: getResponsiveValue(12, 14, 16),
    color: "#6B7280",
  },
  headerActions: {
    flexDirection: "row",
    alignItems: "center",
    gap: getResponsiveValue(8, 12, 16),
  },
  refreshButton: {
    padding: getResponsiveValue(6, 8, 10),
    borderRadius: 8,
    backgroundColor: "#F3F4F6",
  },
  contentContainer: {
    flex: 1,
    margin: getResponsiveValue(12, 16, 20),
  },
  // Card View Styles
  cardContainer: {
    flex: 1,
  },
  historyCard: {
    backgroundColor: "#FFFFFF",
    borderRadius: 12,
    padding: getResponsiveValue(16, 20, 24),
    marginBottom: getResponsiveValue(12, 16, 20),
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 2,
  },
  cardHeader: {
    flexDirection: "row",
    alignItems: "center",
    marginBottom: 12,
  },
  cardIcon: {
    marginRight: 12,
  },
  cardHeaderText: {
    flex: 1,
  },
  cardAction: {
    fontSize: getResponsiveValue(14, 16, 18),
    fontWeight: "600",
    color: "#374151",
  },
  cardDate: {
    fontSize: getResponsiveValue(12, 14, 16),
    color: "#6B7280",
    marginTop: 2,
  },
  cardUser: {
    fontSize: getResponsiveValue(12, 14, 16),
    color: "#6B7280",
  },
  expandButton: {
    padding: 8,
  },
  cardSummary: {
    marginBottom: 12,
  },
  summaryText: {
    fontSize: getResponsiveValue(12, 14, 16),
    color: "#6B7280",
  },
  cardChanges: {
    marginTop: 12,
    paddingTop: 12,
    borderTopWidth: 1,
    borderTopColor: "#F1F5F9",
  },
  changesTitle: {
    fontSize: getResponsiveValue(12, 14, 16),
    fontWeight: "600",
    color: "#374151",
    marginBottom: 8,
  },
  changeItem: {
    marginBottom: 12,
    paddingLeft: 8,
    borderLeftWidth: 3,
    borderLeftColor: "#E5E7EB",
  },
  fieldName: {
    fontSize: getResponsiveValue(12, 14, 16),
    fontWeight: "600",
    color: "#111827",
    marginBottom: 4,
  },
  changeRow: {
    flexDirection: "row",
    marginBottom: 2,
  },
  changeLabel: {
    fontSize: getResponsiveValue(10, 12, 14),
    color: "#6B7280",
    width: 60,
  },
  beforeValue: {
    fontSize: getResponsiveValue(10, 12, 14),
    color: "#EF4444",
    flex: 1,
  },
  afterValue: {
    fontSize: getResponsiveValue(10, 12, 14),
    color: "#10B981",
    flex: 1,
  },
  cardMetadata: {
    marginTop: 12,
    paddingTop: 8,
    borderTopWidth: 1,
    borderTopColor: "#F1F5F9",
  },
  metadataText: {
    fontSize: getResponsiveValue(10, 12, 14),
    color: "#9CA3AF",
  },
  // Table View Styles
  tableContainer: {
    flex: 1,
    backgroundColor: "#FFFFFF",
    borderRadius: 12,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 8,
    elevation: 4,
  },
  tableHeader: {
    flexDirection: "row",
    paddingHorizontal: getResponsiveValue(16, 20, 24),
    paddingVertical: getResponsiveValue(12, 16, 20),
    backgroundColor: "#F8FAFC",
    borderBottomWidth: 1,
    borderBottomColor: "#E2E8F0",
    borderTopLeftRadius: 12,
    borderTopRightRadius: 12,
  },
  tableBody: {
    flex: 1,
  },
  tableRow: {
    flexDirection: "row",
    paddingHorizontal: getResponsiveValue(16, 20, 24),
    paddingVertical: getResponsiveValue(12, 16, 20),
    borderBottomWidth: 1,
    borderBottomColor: "#F1F5F9",
    alignItems: "center",
  },
  evenRow: {
    backgroundColor: "#FAFBFC",
  },
  actionColumn: {
    flex: 1,
  },
  dateColumn: {
    flex: 1.2,
    flexDirection: "row",
    alignItems: "center",
  },
  userColumn: {
    flex: 1,
  },
  changesColumn: {
    flex: 1,
  },
  detailsColumn: {
    flex: 0.8,
    alignItems: "center",
  },
  headerText: {
    fontSize: getResponsiveValue(10, 12, 14),
    fontWeight: "600",
    color: "#475569",
    textTransform: "uppercase",
    letterSpacing: 0.5,
  },
  actionIndicator: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
  },
  actionText: {
    fontSize: getResponsiveValue(12, 14, 16),
    fontWeight: "600",
  },
  dateText: {
    fontSize: getResponsiveValue(10, 12, 14),
    color: "#6B7280",
  },
  userText: {
    fontSize: getResponsiveValue(12, 14, 16),
    color: "#374151",
    fontWeight: "500",
  },
  changesText: {
    fontSize: getResponsiveValue(10, 12, 14),
    color: "#6B7280",
  },
  detailsButton: {
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: 8,
    paddingVertical: 4,
    backgroundColor: "#F3F4F6",
    borderRadius: 6,
    gap: 4,
  },
  detailsButtonText: {
    fontSize: getResponsiveValue(10, 12, 14),
    color: "#3B82F6",
    fontWeight: "500",
  },
  expandedDetails: {
    paddingHorizontal: getResponsiveValue(16, 20, 24),
    paddingVertical: getResponsiveValue(12, 16, 20),
    backgroundColor: "#F8FAFC",
    borderBottomWidth: 1,
    borderBottomColor: "#E2E8F0",
  },
  expandedTitle: {
    fontSize: getResponsiveValue(12, 14, 16),
    fontWeight: "600",
    color: "#374151",
    marginBottom: 8,
  },
  expandedChangeItem: {
    marginBottom: 8,
  },
  expandedFieldName: {
    fontSize: getResponsiveValue(12, 14, 16),
    fontWeight: "600",
    color: "#111827",
  },
  expandedChangeValues: {
    paddingLeft: 12,
  },
  expandedBefore: {
    fontSize: getResponsiveValue(10, 12, 14),
    color: "#EF4444",
  },
  expandedAfter: {
    fontSize: getResponsiveValue(10, 12, 14),
    color: "#10B981",
  },
  expandedMetadata: {
    fontSize: getResponsiveValue(10, 12, 14),
    color: "#9CA3AF",
    marginTop: 8,
    paddingTop: 8,
    borderTopWidth: 1,
    borderTopColor: "#E5E7EB",
  },
  loadingContainer: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    paddingVertical: 60,
  },
  loadingText: {
    marginTop: 12,
    fontSize: getResponsiveValue(12, 14, 16),
    color: "#6B7280",
  },
  emptyState: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    paddingVertical: 80,
  },
  emptyTitle: {
    fontSize: getResponsiveValue(16, 18, 20),
    fontWeight: "600",
    color: "#374151",
    marginTop: 16,
  },
  emptySubtitle: {
    fontSize: getResponsiveValue(12, 14, 16),
    color: "#6B7280",
    marginTop: 8,
    textAlign: "center",
    paddingHorizontal: 20,
  },
  paginationContainer: {
    justifyContent: "space-between",
    alignItems: "center",
    paddingHorizontal: getResponsiveValue(16, 20, 24),
    paddingVertical: getResponsiveValue(12, 16, 20),
    backgroundColor: "#F8FAFC",
    borderBottomLeftRadius: 12,
    borderBottomRightRadius: 12,
    borderTopWidth: 1,
    borderTopColor: "#E2E8F0",
  },
  paginationInfo: {},
  paginationText: {
    fontSize: getResponsiveValue(10, 12, 14),
    color: "#6B7280",
    textAlign: isSmallScreen ? "center" : "left",
  },
  paginationControls: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
  },
  paginationButton: {
    paddingHorizontal: getResponsiveValue(8, 12, 16),
    paddingVertical: getResponsiveValue(6, 8, 10),
    borderRadius: 6,
    backgroundColor: "#FFFFFF",
    borderWidth: 1,
    borderColor: "#E2E8F0",
    minWidth: getResponsiveValue(32, 36, 40),
    alignItems: "center",
  },
  activePage: {
    backgroundColor: "#3B82F6",
    borderColor: "#3B82F6",
  },
  disabledButton: {
    backgroundColor: "#F8FAFC",
    borderColor: "#E2E8F0",
  },
  paginationButtonText: {
    fontSize: getResponsiveValue(10, 12, 14),
    color: "#374151",
    fontWeight: "500",
  },
  activePageText: {
    color: "#FFFFFF",
  },
});

export default OperationHistoryScreen;
